import api from '@/client/axiosInstance';
import type {
  PopupAudience,
  PopupContent,
  PopupImage,
  PopupStatus,
} from '@promo-popup-contract';

// TCX-917 — the FE mirror of the backend's AdminPopupView (promo-popup.service.ts).
// `publishedAt` is `Date | null` server-side but crosses the wire as JSON, so it
// lands here as `string | null`, same convention as legalDocuments.ts's PublishResult.
export interface AdminPopupView {
  audience: PopupAudience;
  isDisabled: boolean;
  published: PopupContent | null;
  draft: PopupContent | null;
  publishedAt: string | null;
  status: PopupStatus;
  hasPendingDraft: boolean;
}

export const getAdminPopup = async (
  audience: PopupAudience,
): Promise<AdminPopupView> =>
  (await api.get(`/promo-popup/admin/${audience}`)).data;

/**
 * TCX-917 review (finding 1) — `imageReceipt` is the proof, issued by the
 * upload, that `content.image` is an object this server produced. Sent beside
 * the content rather than inside it: it accompanies the request and is never
 * stored. Omitted when the image has not changed since it was last saved —
 * the server accepts an image already on the record without one.
 */
export const savePopupDraft = async (
  audience: PopupAudience,
  content: PopupContent,
  imageReceipt?: string,
): Promise<AdminPopupView> =>
  (
    await api.put(`/promo-popup/admin/${audience}/draft`, {
      content,
      imageReceipt,
    })
  ).data;

export const publishPopup = async (
  audience: PopupAudience,
): Promise<AdminPopupView> =>
  (await api.post(`/promo-popup/admin/${audience}/publish`)).data;

export const setPopupVisibility = async (
  audience: PopupAudience,
  isDisabled: boolean,
): Promise<AdminPopupView> =>
  (await api.patch(`/promo-popup/admin/${audience}/visibility`, { isDisabled }))
    .data;

/**
 * Returns the receipt beside the image. The caller must keep it and hand it to
 * the next `savePopupDraft`, or the server refuses the image as one it did not
 * issue.
 */
export const uploadPopupImage = async (
  audience: PopupAudience,
  file: File,
): Promise<{ image: PopupImage; receipt: string }> => {
  const form = new FormData();
  form.append('image', file);
  return (await api.post(`/promo-popup/admin/${audience}/image`, form)).data;
};

/**
 * Returns null when the pop-up is off or nothing is published — the server
 * decides, not the caller. The audience is resolved from the access token, so
 * there is no parameter here by design.
 */
export const getMyPopup = async (): Promise<PopupContent | null> =>
  (await api.get('/promo-popup/me')).data ?? null;
